import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { call, put, takeEvery } from "redux-saga/effects";
import { Beach, Seat } from "../../interfaces";
import { Paginated } from "../../interfaces/Paginated";
import { googleFunctionService } from "../../services/axios";
import * as sagaActions from "./sagaActions";
import { actions } from "./slice";
import * as BeachActionTypes from "./types";

export function* getAllBeachSaga({
  payload,
}: ReturnType<typeof sagaActions.loadAllBeach>) {
  yield put(
    actions.setLoader({ fetchableProp: "allBeaches", isLoading: true })
  );

  try {
    const {
      data,
    }: AxiosResponse<Paginated<Beach>> = yield call(
      googleFunctionService.get,
      "/beach",
      { params: payload }
    );
    yield put(actions.setPaginatedBeaches(data));
  } catch (err) {
    actions.setError({ fetchableProp: "allBeaches", error: err });
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "allBeaches", isLoading: false })
    );
  }
}

export function* createBeachSaga({
  payload,
}: PayloadAction<Omit<Beach, "id">>) {
  yield put(
    actions.setLoader({ fetchableProp: "createdBeach", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Beach> = yield call(
      googleFunctionService.post,
      "/beach",
      payload
    );
    yield put(actions.createBeach(data));
  } catch (err) {
    yield put(actions.setError({ fetchableProp: "createdBeach", error: err }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "createdBeach", isLoading: false })
    );
  }
}

export function* createSeatSaga({
  payload,
}: ReturnType<typeof sagaActions.createSeat>) {
  yield put(
    actions.setLoader({ fetchableProp: "updatedBeach", isLoading: true })
  );
  try {
    const { data }: AxiosResponse<Seat> = yield call(
      googleFunctionService.post,
      `/beach/${payload.beachId}/seats`,
      payload.seat
    );
    yield put(actions.createSeat(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "updatedBeach", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "updatedBeach", isLoading: false })
    );
  }
}

export function* updateBeachSaga({
  payload,
}: ReturnType<typeof sagaActions.updateBeach>) {
  yield put(
    actions.setLoader({ fetchableProp: "updatedBeach", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Beach> = yield call(
      googleFunctionService,
      `/beach/${payload.id}`,
      {
        method: "PUT",
        data: payload,
      }
    );
    yield put(actions.updateBeach(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "updatedBeach", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "updatedBeach", isLoading: false })
    );
  }
}

export function* deleteSeatSaga({
  payload,
}: ReturnType<typeof sagaActions.deleteSeat>) {
  yield put(
    actions.setLoader({ fetchableProp: "updatedBeach", isLoading: true })
  );

  try {
    yield call(
      googleFunctionService,
      `/beach/${payload.beachId}/seats/${payload.seatId}`,
      {
        method: "DELETE",
      }
    );
    yield put(actions.deleteSeat(payload.seatId));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "updatedBeach", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "updatedBeach", isLoading: false })
    );
  }
}

export function* updateSeatSaga({
  payload,
}: ReturnType<typeof sagaActions.updateSeat>) {
  yield put(
    actions.setLoader({ fetchableProp: "updatedBeach", isLoading: true })
  );

  try {
    yield call(
      googleFunctionService,
      `/beach/${payload.beachId}/seats/${payload.seat.id}`,
      {
        method: "PUT",
        data: payload.seat,
      }
    );
    yield put(actions.updateSeat(payload.seat));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "updatedBeach", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "updatedBeach", isLoading: false })
    );
  }
}

export function* deleteBeachSaga({
  payload,
}: ReturnType<typeof sagaActions.deleteBeachById>) {
  yield put(
    actions.setLoader({ fetchableProp: "deletedBeach", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Beach> = yield call(
      googleFunctionService.delete,
      `/beach/${payload}`
    );
    yield put(actions.deleteBeach(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "deletedBeach", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "deletedBeach", isLoading: false })
    );
  }
}

export function* getBeachByIdSaga({
  payload,
}: ReturnType<typeof sagaActions.loadBeachById>) {
  yield put(actions.setLoader({ fetchableProp: "beach", isLoading: true }));

  try {
    const { data }: AxiosResponse<Beach> = yield call(
      googleFunctionService.get,
      `/beach/${payload}`
    );
    yield put(actions.setBeach(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "beach", error }));
  } finally {
    yield put(actions.setLoader({ fetchableProp: "beach", isLoading: false }));
  }
}

export default function* rootSaga() {
  yield takeEvery(
    BeachActionTypes.fetch_paginated_beaches_saga,
    getAllBeachSaga
  );
  yield takeEvery(BeachActionTypes.fetch_beach_by_id_saga, getBeachByIdSaga);
  yield takeEvery(BeachActionTypes.create_new_beach_saga, createBeachSaga);
  yield takeEvery(BeachActionTypes.delete_beach_saga, deleteBeachSaga);
  yield takeEvery(BeachActionTypes.update_beach_saga, updateBeachSaga);
  yield takeEvery(BeachActionTypes.create_seat_saga, createSeatSaga);
  yield takeEvery(BeachActionTypes.delete_seat_saga, deleteSeatSaga);
  yield takeEvery(BeachActionTypes.update_seat_saga, updateSeatSaga);
}
